import { inject, injectable } from 'inversify';
import { type Genql } from '@bizzdesign/bizzdesign-sdk-type-genql';
import {
  ApiError,
  isNotUndefinedOrNull,
  type Logger,
  TYPES,
} from '@bizzdesign/sdk-internal';
import { assertIsUUIDs } from '@bizzdesign/sdk-internal/uuid';
import { type LowLevelClient } from '@bizzdesign/sdk-lowlevel';
import { CacheableResultWrapper } from '@bizzdesign/sdk-knowledgegraph';
import { EMPTY, filter, map, merge, mergeMap, of, type Observable } from 'rxjs';
import { Err, Ok, type Result as TsResult } from 'ts-results';
import {
  ALL_COMMENT_EVENT_TYPES,
  type ChatClient,
  type CommentCoordinates,
  type CommentEvent,
  type CommentEventFilter,
  type CommentEventType,
  type CommentReactionMutationOutput,
  type CommentReactionParams,
  type CommentThread,
  type CommentThreadSort,
  type CommentThreadsFilter,
  type CommentThreadReference,
  type Comment,
  type CommentsFilter,
  type CreateCommentThreadOutput,
  type CreateCommentThreadParams,
  type DeleteCommentsOutput,
  type DeleteCommentsParams,
  type DeleteCommentThreadsOutput,
  type DeleteCommentThreadsParams,
  type GetCommentsForThreadOptions,
  type MarkCommentsOutput,
  type MarkCommentsParams,
  type ReplyToCommentThreadParams,
  type ResolveCommentThreadOutput,
  type ResolveCommentThreadParams,
  type UpdateCommentOutput,
  type UpdateCommentParams,
  type UpdateCommentThreadOutput,
  type UpdateCommentThreadParams,
  type Result,
} from './interfaces';
import {
  type GenqlCommentEventPayload,
  type GenqlCommentLike,
  type GenqlCommentThreadLike,
  mapComment,
  mapCommentThread,
  mapGenqlCommentEventPayload,
  toGenqlCommentThreadSort,
} from './mapper.utils';

/**
 * `coordinates` is required by the schema but is ignored by the backend when
 * replying to an existing thread (the position is only meaningful for the
 * thread's first comment). A neutral default keeps the payload valid.
 */
const DEFAULT_REPLY_COORDINATES: CommentCoordinates = { x: 0, y: 0 };

function toUUIDCommentIds(
  commentIds: string[],
): MarkCommentsOutput['commentIds'] {
  assertIsUUIDs(commentIds);
  return commentIds;
}

function toValidatedSelectorIds(
  ids: readonly string[] | undefined,
): string[] | undefined {
  if (ids === undefined) {
    return undefined;
  }
  const clonedIds = [...ids];
  assertIsUUIDs(clonedIds);
  return clonedIds;
}

function toGenqlCommentThreadsFilter(
  filter: CommentThreadsFilter,
): Genql.CommentThreadsFilter {
  return {
    parent: { type: filter.parent.type, id: filter.parent.id },
    ...(filter.searchText !== undefined
      ? { searchText: filter.searchText }
      : {}),
  };
}

function toGenqlCommentsFilter(filter: CommentsFilter): Genql.CommentsFilter {
  return {
    threadId: filter.threadId,
    ...(filter.searchText !== undefined
      ? { searchText: filter.searchText }
      : {}),
  };
}

function hasSearchText(searchText: string | undefined): boolean {
  return (searchText?.trim().length ?? 0) > 0;
}

const THREAD_SUBSCRIPTION_EVENT_TYPES: readonly CommentEventType[] = [
  'CREATE_COMMENT',
  'UPDATE_COMMENT_THREAD',
  'DELETE_COMMENT_THREAD',
  'RESOLVE_COMMENT_THREAD',
];

const COMMENT_SUBSCRIPTION_EVENT_TYPES: readonly CommentEventType[] = [
  'UPDATE_COMMENT',
  'DELETE_COMMENT',
  'ADD_COMMENT_REACTION',
  'REMOVE_COMMENT_REACTION',
];

type MutationResponse<TSuccess> = {
  success: TSuccess | null;
  errors: Array<{ message: string }>;
};

type CommentMutationOperation =
  | 'createComment'
  | 'deleteComments'
  | 'deleteCommentThreads'
  | 'updateComment'
  | 'updateCommentThread'
  | 'resolveCommentThread'
  | 'markCommentsAsRead'
  | 'markCommentsAsUnread'
  | 'addCommentReaction'
  | 'removeCommentReaction';

function getCommentSubscriptionEvents(
  filterParam: CommentEventFilter | undefined,
): readonly CommentEventType[] {
  const eventTypes = filterParam?.eventTypes ?? ALL_COMMENT_EVENT_TYPES;
  if (eventTypes.length === 0) {
    throw new ApiError('eventTypes must contain at least one event type');
  }
  return eventTypes;
}

// AppSync mutation-response invalidation supports five calls and five `in`/`notIn` values, so merge domain streams.
// https://docs.aws.amazon.com/appsync/latest/devguide/extensions.html#extensions-invalidationJsonObject
function getCommentSubscriptionEventGroups(
  eventTypes: readonly CommentEventType[],
): readonly (readonly CommentEventType[])[] {
  return [
    THREAD_SUBSCRIPTION_EVENT_TYPES,
    COMMENT_SUBSCRIPTION_EVENT_TYPES,
  ].flatMap((subscriptionEventTypes) => {
    const matchingEventTypes = eventTypes.filter((eventType) =>
      subscriptionEventTypes.includes(eventType),
    );
    return matchingEventTypes.length === 0 ? [] : [matchingEventTypes];
  });
}

@injectable()
export class ChatClientImpl implements ChatClient {
  constructor(
    @inject(TYPES.LowLevelClient)
    private readonly lowLevelClient: LowLevelClient,
    @inject(TYPES.Logger) private readonly logger: Logger,
  ) {}

  private readonly DEFAULT_PAGE_OFFSET = 0;
  private readonly DEFAULT_PAGE_LIMIT = 50;

  private readonly userInfoSelection = {
    id: true,
    username: true,
    firstName: true,
    lastName: true,
    email: true,
    __typename: true,
  } satisfies Genql.UserInfoGenqlSelection;

  private readonly userReferenceSelection = {
    id: true,
    __typename: true,
  } satisfies Genql.UserInfoGenqlSelection;

  private readonly systemAttributesSelection = {
    createdAt: true,
    createdBy: this.userInfoSelection,
    updatedAt: true,
    updatedBy: this.userInfoSelection,
    hiddenAt: true,
    hiddenBy: this.userInfoSelection,
    isHidden: true,
    __typename: true,
  } satisfies Genql.SystemAttributesGenqlSelection;

  private readonly commentReactionSelection = {
    emoji: true,
    users: this.userInfoSelection,
    __typename: true,
  } satisfies Genql.CommentReactionGenqlSelection;

  private readonly commentSelection = {
    id: true,
    threadId: true,
    text: true,
    author: this.userInfoSelection,
    reactions: this.commentReactionSelection,
    readBy: this.userReferenceSelection,
    systemAttributes: this.systemAttributesSelection,
    __typename: true,
  } satisfies Genql.CommentGenqlSelection;

  private readonly commentThreadSelection = {
    id: true,
    parentType: true,
    parentId: true,
    coordinates: { x: true, y: true, __typename: true },
    graphicId: true,
    resolved: true,
    resolvedAt: true,
    comments: this.commentSelection,
    systemAttributes: this.systemAttributesSelection,
    __typename: true,
  } satisfies Genql.CommentThreadGenqlSelection;

  private readonly commentCreatedPayloadSelection: Genql.CommentCreatedPayloadGenqlSelection =
    {
      threadId: true,
      parentType: true,
      parentId: true,
      coordinates: { x: true, y: true, __typename: true },
      graphicId: true,
      resolved: true,
      resolvedAt: true,
      threadSystemAttributes: this.systemAttributesSelection,
      comment: this.commentSelection,
      __typename: true,
    };

  private readonly commentUpdatedPayloadSelection: Genql.CommentUpdatedPayloadGenqlSelection =
    {
      threadId: true,
      commentId: true,
      text: true,
      systemAttributes: this.systemAttributesSelection,
      __typename: true,
    };

  private readonly commentsDeletedPayloadSelection: Genql.CommentsDeletedPayloadGenqlSelection =
    {
      threadId: true,
      commentIds: true,
      deleteThread: true,
      __typename: true,
    };

  private readonly commentThreadUpdatedPayloadSelection: Genql.CommentThreadUpdatedPayloadGenqlSelection =
    {
      threadId: true,
      coordinates: { x: true, y: true, __typename: true },
      graphicId: true,
      threadSystemAttributes: this.systemAttributesSelection,
      commentId: true,
      text: true,
      commentSystemAttributes: this.systemAttributesSelection,
      __typename: true,
    };

  private readonly commentThreadResolvedPayloadSelection: Genql.CommentThreadResolvedPayloadGenqlSelection =
    {
      threadId: true,
      resolved: true,
      resolvedAt: true,
      systemAttributes: this.systemAttributesSelection,
      __typename: true,
    };

  private readonly commentThreadDeletedPayloadSelection: Genql.CommentThreadDeletedPayloadGenqlSelection =
    {
      threadId: true,
      __typename: true,
    };

  private readonly commentReactionPayloadSelection: Genql.CommentReactionPayloadGenqlSelection =
    {
      commentId: true,
      threadId: true,
      emoji: true,
      user: this.userInfoSelection,
      __typename: true,
    };

  private readonly genqlBaseError = {
    message: true,
    ids: true,
    __typename: true,
  } as const satisfies Genql.InvalidInputErrorGenqlSelection;

  private readonly commonMutationErrors = {
    on_InvalidInputError: this.genqlBaseError,
    on_ForbiddenError: this.genqlBaseError,
    on_UnauthorizedError: this.genqlBaseError,
  } as const;

  async createCommentThread({
    parent,
    text,
    coordinates,
    graphicId,
  }: CreateCommentThreadParams): Promise<
    TsResult<CreateCommentThreadOutput, ApiError>
  > {
    return this.createComment({
      parent: { type: parent.type, id: parent.id },
      text,
      coordinates,
      graphicId,
    });
  }

  async replyToCommentThread({
    threadId,
    text,
  }: ReplyToCommentThreadParams): Promise<
    TsResult<CreateCommentThreadOutput, ApiError>
  > {
    return this.createComment({
      threadId,
      text,
      coordinates: DEFAULT_REPLY_COORDINATES,
    });
  }

  async updateComment({
    id,
    text,
  }: UpdateCommentParams): Promise<TsResult<UpdateCommentOutput, ApiError>> {
    return this.updateCommentMutation({
      id,
      text,
    });
  }

  async deleteComments({
    ids,
  }: DeleteCommentsParams): Promise<TsResult<DeleteCommentsOutput, ApiError>> {
    return this.deleteCommentsMutation({ ids });
  }

  async updateCommentThread({
    id,
    text,
    coordinates,
    graphicId,
  }: UpdateCommentThreadParams): Promise<
    TsResult<UpdateCommentThreadOutput, ApiError>
  > {
    return this.updateCommentThreadMutation({
      id,
      text,
      coordinates,
      graphicId,
    });
  }

  async deleteCommentThreads({
    ids,
  }: DeleteCommentThreadsParams): Promise<
    TsResult<DeleteCommentThreadsOutput, ApiError>
  > {
    return this.deleteCommentThreadsMutation({ ids });
  }

  async resolveCommentThread({
    id,
    resolved,
  }: ResolveCommentThreadParams): Promise<
    TsResult<ResolveCommentThreadOutput, ApiError>
  > {
    return this.resolveCommentThreadMutation({ id, resolved });
  }

  async markCommentsAsRead({
    commentIds,
    threadIds,
  }: MarkCommentsParams): Promise<TsResult<MarkCommentsOutput, ApiError>> {
    return this.markCommentsAsReadMutation({
      commentIds: toValidatedSelectorIds(commentIds),
      threadIds: toValidatedSelectorIds(threadIds),
    });
  }

  async markCommentsAsUnread({
    commentIds,
    threadIds,
  }: MarkCommentsParams): Promise<TsResult<MarkCommentsOutput, ApiError>> {
    return this.markCommentsAsUnreadMutation({
      commentIds: toValidatedSelectorIds(commentIds),
      threadIds: toValidatedSelectorIds(threadIds),
    });
  }

  async addCommentReaction({
    commentId,
    emoji,
  }: CommentReactionParams): Promise<
    TsResult<CommentReactionMutationOutput, ApiError>
  > {
    return this.commentReactionMutation('addCommentReaction', {
      commentId,
      emoji,
    });
  }

  async removeCommentReaction({
    commentId,
    emoji,
  }: CommentReactionParams): Promise<
    TsResult<CommentReactionMutationOutput, ApiError>
  > {
    return this.commentReactionMutation('removeCommentReaction', {
      commentId,
      emoji,
    });
  }

  onCommentEvents$(filterParam?: CommentEventFilter): Observable<CommentEvent> {
    const eventTypes = getCommentSubscriptionEvents(filterParam);
    return merge(
      ...getCommentSubscriptionEventGroups(eventTypes).map(
        (subscriptionEventTypes) =>
          this.onCommentEventsForTypes$(subscriptionEventTypes),
      ),
    );
  }

  private onCommentEventsForTypes$(
    eventTypes: readonly CommentEventType[],
  ): Observable<CommentEvent> {
    const includeCreateComment = eventTypes.includes('CREATE_COMMENT');
    const includeUpdateComment = eventTypes.includes('UPDATE_COMMENT');
    const includeDeleteComment = eventTypes.includes('DELETE_COMMENT');
    const includeUpdateCommentThread = eventTypes.includes(
      'UPDATE_COMMENT_THREAD',
    );
    const includeResolveCommentThread = eventTypes.includes(
      'RESOLVE_COMMENT_THREAD',
    );
    const includeDeleteCommentThread = eventTypes.includes(
      'DELETE_COMMENT_THREAD',
    );
    const includeReactionChanged =
      eventTypes.includes('ADD_COMMENT_REACTION') ||
      eventTypes.includes('REMOVE_COMMENT_REACTION');
    const selection = {
      onEvent: {
        __args: {
          events: [...eventTypes],
        },
        event: true,
        commentEvent: {
          ...(includeCreateComment
            ? { commentCreated: this.commentCreatedPayloadSelection }
            : {}),
          ...(includeUpdateComment
            ? { commentUpdated: this.commentUpdatedPayloadSelection }
            : {}),
          ...(includeDeleteComment
            ? { commentsDeleted: this.commentsDeletedPayloadSelection }
            : {}),
          ...(includeUpdateCommentThread
            ? { threadUpdated: this.commentThreadUpdatedPayloadSelection }
            : {}),
          ...(includeResolveCommentThread
            ? { threadResolved: this.commentThreadResolvedPayloadSelection }
            : {}),
          ...(includeDeleteCommentThread
            ? { threadDeleted: this.commentThreadDeletedPayloadSelection }
            : {}),
          ...(includeReactionChanged
            ? { reactionChanged: this.commentReactionPayloadSelection }
            : {}),
          __typename: true,
        },
        __typename: true,
      },
    } satisfies Genql.SubscriptionGenqlSelection;

    return this.lowLevelClient
      .subscription(selection, { resubscribeOnReconnect: true })
      .pipe(
        map((payload) => payload.data?.onEvent),
        filter((genqlEvent): genqlEvent is GenqlCommentEventPayload =>
          isNotUndefinedOrNull(genqlEvent),
        ),
        mergeMap((genqlEvent) => {
          try {
            const commentEvent = mapGenqlCommentEventPayload(genqlEvent);
            if (commentEvent === null) {
              this.logger.warn({
                context: 'ChatClient',
                msg: 'Unmappable comment subscription payload',
                payload: { event: genqlEvent.event },
              });
              return EMPTY;
            }
            return of(commentEvent);
          } catch (error) {
            this.logger.warn({
              context: 'ChatClient',
              msg: 'Failed to map comment subscription payload',
              payload: { event: genqlEvent.event },
              error,
            });
            return EMPTY;
          }
        }),
      );
  }

  isCommentThreadLocked(thread: CommentThread): boolean {
    return thread.resolved;
  }

  getCommentThreads(
    filter: CommentThreadsFilter,
    orderBy?: CommentThreadSort,
  ): Result<CommentThread> {
    let lastTotalCount: number | undefined;
    return new CacheableResultWrapper<CommentThread>(
      'ChatClient',
      (prefetchSize) =>
        this.processPaginatedCommentThreads(
          filter,
          orderBy,
          this.DEFAULT_PAGE_OFFSET,
          prefetchSize,
          (totalCount) => {
            lastTotalCount = totalCount;
          },
        ),
      () =>
        lastTotalCount !== undefined
          ? Promise.resolve(lastTotalCount)
          : this.getCommentThreadsCount(filter),
      (limit, offset) =>
        this.getCommentThreadsPage(filter, orderBy, offset, limit).then(
          (page) => {
            lastTotalCount = page.totalCount;
            return page.threads;
          },
        ),
      {
        streamPrefetchSize: this.DEFAULT_PAGE_LIMIT,
        getAllPrefetchSize: this.DEFAULT_PAGE_LIMIT,
        logger: this.logger,
      },
    );
  }

  getComments(filter: CommentsFilter): Result<Comment> {
    let lastTotalCount: number | undefined;
    return new CacheableResultWrapper<Comment>(
      'ChatClient',
      (prefetchSize) =>
        this.processPaginatedComments(
          filter,
          this.DEFAULT_PAGE_OFFSET,
          prefetchSize,
          (totalCount) => {
            lastTotalCount = totalCount;
          },
        ),
      () =>
        lastTotalCount !== undefined
          ? Promise.resolve(lastTotalCount)
          : this.getCommentsCount(filter),
      (limit, offset) =>
        this.getCommentsPage(filter, offset, limit).then((page) => {
          lastTotalCount = page.totalCount;
          return page.comments;
        }),
      {
        streamPrefetchSize: this.DEFAULT_PAGE_LIMIT,
        getAllPrefetchSize: this.DEFAULT_PAGE_LIMIT,
        logger: this.logger,
      },
    );
  }

  getCommentsForThread(
    thread: CommentThreadReference,
    options?: GetCommentsForThreadOptions,
  ): Result<Comment> {
    return this.getComments({
      threadId: thread.id,
      searchText: options?.searchText,
    });
  }

  private async getCommentThreadsCount(
    filter: CommentThreadsFilter,
  ): Promise<number> {
    const result = await this.lowLevelClient.query({
      commentThreads: {
        __args: {
          filter: toGenqlCommentThreadsFilter(filter),
          page: { offset: this.DEFAULT_PAGE_OFFSET, limit: 1 },
        },
        totalCount: true,
        __typename: true,
      } satisfies Genql.QueryGenqlSelection['commentThreads'],
    });
    return result.commentThreads.totalCount ?? 0;
  }

  private async getCommentThreadsPage(
    filter: CommentThreadsFilter,
    orderBy: CommentThreadSort | undefined,
    offset: number,
    limit: number,
  ): Promise<{ threads: CommentThread[]; totalCount: number }> {
    this.logger.debug({
      context: 'ChatClient',
      msg: 'Request to commentThreads',
      payload: {
        filter: {
          parent: filter.parent,
          hasSearchText: hasSearchText(filter.searchText),
        },
        offset,
        limit,
        orderBy,
      },
    });

    const result = await this.lowLevelClient.query({
      commentThreads: {
        __args: {
          filter: toGenqlCommentThreadsFilter(filter),
          page: { offset, limit },
          orderBy: toGenqlCommentThreadSort(orderBy),
        },
        threads: this.commentThreadSelection,
        totalCount: true,
        __typename: true,
      } satisfies Genql.QueryGenqlSelection['commentThreads'],
    });

    this.logger.debug({
      context: 'ChatClient',
      msg: 'Result from commentThreads',
      payload: {
        threadCount: result.commentThreads.threads.length,
        totalCount: result.commentThreads.totalCount,
      },
    });

    return {
      threads: result.commentThreads.threads.map(mapCommentThread),
      totalCount: result.commentThreads.totalCount ?? 0,
    };
  }

  private async getCommentsCount(filter: CommentsFilter): Promise<number> {
    const result = await this.lowLevelClient.query({
      comments: {
        __args: {
          filter: toGenqlCommentsFilter(filter),
          page: { offset: this.DEFAULT_PAGE_OFFSET, limit: 1 },
        },
        totalCount: true,
        __typename: true,
      } satisfies Genql.QueryGenqlSelection['comments'],
    });
    return result.comments.totalCount ?? 0;
  }

  private async getCommentsPage(
    filter: CommentsFilter,
    offset: number,
    limit: number,
  ): Promise<{ comments: Comment[]; totalCount: number }> {
    this.logger.debug({
      context: 'ChatClient',
      msg: 'Request to comments',
      payload: {
        threadId: filter.threadId,
        hasSearchText: hasSearchText(filter.searchText),
        offset,
        limit,
      },
    });

    const result = await this.lowLevelClient.query({
      comments: {
        __args: {
          filter: toGenqlCommentsFilter(filter),
          page: { offset, limit },
        },
        comments: this.commentSelection,
        totalCount: true,
        __typename: true,
      } satisfies Genql.QueryGenqlSelection['comments'],
    });

    this.logger.debug({
      context: 'ChatClient',
      msg: 'Result from comments',
      payload: {
        commentCount: result.comments.comments.length,
        totalCount: result.comments.totalCount,
      },
    });

    return {
      comments: result.comments.comments.map((comment) =>
        mapComment(comment as GenqlCommentLike),
      ),
      totalCount: result.comments.totalCount ?? 0,
    };
  }

  private async *processPaginatedCommentThreads(
    filter: CommentThreadsFilter,
    orderBy: CommentThreadSort | undefined,
    offset: number,
    limit: number,
    totalCountCallback: (totalCount: number) => void,
  ): AsyncGenerator<CommentThread> {
    const page = await this.getCommentThreadsPage(
      filter,
      orderBy,
      offset,
      limit,
    );
    totalCountCallback(page.totalCount);
    for (const thread of page.threads) {
      yield thread;
    }

    if (page.threads.length >= limit) {
      yield* this.processPaginatedCommentThreads(
        filter,
        orderBy,
        offset + limit,
        limit,
        totalCountCallback,
      );
    }
  }

  private async *processPaginatedComments(
    filter: CommentsFilter,
    offset: number,
    limit: number,
    totalCountCallback: (totalCount: number) => void,
  ): AsyncGenerator<Comment> {
    const page = await this.getCommentsPage(filter, offset, limit);
    totalCountCallback(page.totalCount);
    for (const comment of page.comments) {
      yield comment;
    }

    if (page.comments.length >= limit) {
      yield* this.processPaginatedComments(
        filter,
        offset + limit,
        limit,
        totalCountCallback,
      );
    }
  }

  private async updateCommentMutation(
    input: Genql.UpdateCommentInput,
  ): Promise<TsResult<UpdateCommentOutput, ApiError>> {
    return this.executeMutation({
      operation: 'updateComment',
      requestPayload: {
        id: input.id,
      },
      noResultMessage: 'updateComment returned no result',
      runMutation: () =>
        this.lowLevelClient.mutation({
          updateComment: {
            __args: { with: input },
            success: {
              comment: this.commentSelection,
              thread: this.commentThreadSelection,
            },
            errors: this.commentNotFoundErrors(),
            __typename: true,
          } satisfies Genql.MutationGenqlSelection['updateComment'],
        }),
      extractResponse: (result) => result.updateComment,
      buildResultPayload: (response) => ({
        hasSuccess: response.success !== null,
        errorCount: response.errors.length,
        commentId: response.success?.comment.id,
        threadId: response.success?.thread.id,
      }),
      mapSuccess: (success) => ({
        comment: mapComment(success.comment),
        thread: mapCommentThread(success.thread),
      }),
    });
  }

  private async deleteCommentsMutation(
    input: Genql.DeleteCommentsInput,
  ): Promise<TsResult<DeleteCommentsOutput, ApiError>> {
    return this.executeMutation({
      operation: 'deleteComments',
      requestPayload: { ids: input.ids },
      noResultMessage: 'deleteComments returned no result',
      runMutation: () =>
        this.lowLevelClient.mutation({
          deleteComments: {
            __args: { with: input },
            success: {
              comments: this.commentSelection,
              threads: this.commentThreadSelection,
            },
            errors: this.commentNotFoundErrors(),
            __typename: true,
          } satisfies Genql.MutationGenqlSelection['deleteComments'],
        }),
      extractResponse: (result) => result.deleteComments,
      buildResultPayload: (response) => ({
        hasSuccess: response.success !== null,
        errorCount: response.errors.length,
        commentCount: response.success?.comments.length,
        threadCount: response.success?.threads.length,
      }),
      mapSuccess: (success) => ({
        comments: success.comments.map(mapComment),
        threads: success.threads.map(mapCommentThread),
      }),
    });
  }

  private async updateCommentThreadMutation(
    input: Genql.UpdateCommentThreadInput,
  ): Promise<TsResult<UpdateCommentThreadOutput, ApiError>> {
    return this.executeMutation({
      operation: 'updateCommentThread',
      requestPayload: {
        id: input.id,
        hasGraphicId: input.graphicId !== undefined && input.graphicId !== null,
      },
      noResultMessage: 'updateCommentThread returned no result',
      runMutation: () =>
        this.lowLevelClient.mutation({
          updateCommentThread: {
            __args: { with: input },
            success: {
              comment: this.commentSelection,
              thread: this.commentThreadSelection,
            },
            errors: this.commentNotFoundErrors(),
            __typename: true,
          } satisfies Genql.MutationGenqlSelection['updateCommentThread'],
        }),
      extractResponse: (result) => result.updateCommentThread,
      buildResultPayload: (response) => ({
        hasSuccess: response.success !== null,
        errorCount: response.errors.length,
        commentId: response.success?.comment.id,
        threadId: response.success?.thread.id,
      }),
      mapSuccess: (success) => ({
        comment: mapComment(success.comment),
        thread: mapCommentThread(success.thread),
      }),
    });
  }

  private async deleteCommentThreadsMutation(
    input: Genql.DeleteCommentThreadsInput,
  ): Promise<TsResult<DeleteCommentThreadsOutput, ApiError>> {
    return this.executeMutation({
      operation: 'deleteCommentThreads',
      requestPayload: { ids: input.ids },
      noResultMessage: 'deleteCommentThreads returned no result',
      runMutation: () =>
        this.lowLevelClient.mutation({
          deleteCommentThreads: {
            __args: { with: input },
            success: {
              threads: this.commentThreadSelection,
            },
            errors: this.commentNotFoundErrors(),
            __typename: true,
          } satisfies Genql.MutationGenqlSelection['deleteCommentThreads'],
        }),
      extractResponse: (result) => result.deleteCommentThreads,
      buildResultPayload: (response) => ({
        hasSuccess: response.success !== null,
        errorCount: response.errors.length,
        threadCount: response.success?.threads.length,
      }),
      mapSuccess: (success) => ({
        threads: success.threads.map(mapCommentThread),
      }),
    });
  }

  private async resolveCommentThreadMutation(
    input: Genql.ResolveCommentThreadInput,
  ): Promise<TsResult<ResolveCommentThreadOutput, ApiError>> {
    return this.executeMutation({
      operation: 'resolveCommentThread',
      requestPayload: { ...input },
      noResultMessage: 'resolveCommentThread returned no result',
      runMutation: () =>
        this.lowLevelClient.mutation({
          resolveCommentThread: {
            __args: { with: input },
            success: {
              thread: this.commentThreadSelection,
            },
            errors: this.commentNotFoundErrors(),
            __typename: true,
          } satisfies Genql.MutationGenqlSelection['resolveCommentThread'],
        }),
      extractResponse: (result) => result.resolveCommentThread,
      buildResultPayload: (response) => ({
        hasSuccess: response.success !== null,
        errorCount: response.errors.length,
        threadId: response.success?.thread.id,
        resolved: response.success?.thread.resolved,
      }),
      mapSuccess: (success) => ({
        thread: mapCommentThread(success.thread),
      }),
    });
  }

  private async markCommentsAsReadMutation(
    input: Genql.MarkCommentsAsReadInput,
  ): Promise<TsResult<MarkCommentsOutput, ApiError>> {
    return this.executeMutation({
      operation: 'markCommentsAsRead',
      requestPayload: {
        commentIds: input.commentIds,
        threadIds: input.threadIds,
      },
      noResultMessage: 'markCommentsAsRead returned no result',
      runMutation: () =>
        this.lowLevelClient.mutation({
          markCommentsAsRead: {
            __args: { with: input },
            success: {
              commentIds: true,
            },
            errors: this.commentNotFoundErrors(),
            __typename: true,
          } satisfies Genql.MutationGenqlSelection['markCommentsAsRead'],
        }),
      extractResponse: (result) => result.markCommentsAsRead,
      buildResultPayload: (response) => ({
        hasSuccess: response.success !== null,
        errorCount: response.errors.length,
        commentCount: response.success?.commentIds.length,
      }),
      mapSuccess: (success) => ({
        commentIds: toUUIDCommentIds(success.commentIds),
      }),
    });
  }

  private async markCommentsAsUnreadMutation(
    input: Genql.MarkCommentsAsUnreadInput,
  ): Promise<TsResult<MarkCommentsOutput, ApiError>> {
    return this.executeMutation({
      operation: 'markCommentsAsUnread',
      requestPayload: {
        commentIds: input.commentIds,
        threadIds: input.threadIds,
      },
      noResultMessage: 'markCommentsAsUnread returned no result',
      runMutation: () =>
        this.lowLevelClient.mutation({
          markCommentsAsUnread: {
            __args: { with: input },
            success: {
              commentIds: true,
            },
            errors: this.commentNotFoundErrors(),
            __typename: true,
          } satisfies Genql.MutationGenqlSelection['markCommentsAsUnread'],
        }),
      extractResponse: (result) => result.markCommentsAsUnread,
      buildResultPayload: (response) => ({
        hasSuccess: response.success !== null,
        errorCount: response.errors.length,
        commentCount: response.success?.commentIds.length,
      }),
      mapSuccess: (success) => ({
        commentIds: toUUIDCommentIds(success.commentIds),
      }),
    });
  }

  private async commentReactionMutation(
    operation: 'addCommentReaction',
    input: Genql.AddCommentReactionInput,
  ): Promise<TsResult<CommentReactionMutationOutput, ApiError>>;
  private async commentReactionMutation(
    operation: 'removeCommentReaction',
    input: Genql.RemoveCommentReactionInput,
  ): Promise<TsResult<CommentReactionMutationOutput, ApiError>>;
  private async commentReactionMutation(
    operation: 'addCommentReaction' | 'removeCommentReaction',
    input: Genql.AddCommentReactionInput | Genql.RemoveCommentReactionInput,
  ): Promise<TsResult<CommentReactionMutationOutput, ApiError>> {
    return operation === 'addCommentReaction'
      ? this.addCommentReactionMutation(input)
      : this.removeCommentReactionMutation(input);
  }

  private async addCommentReactionMutation(
    input: Genql.AddCommentReactionInput,
  ): Promise<TsResult<CommentReactionMutationOutput, ApiError>> {
    return this.executeMutation({
      operation: 'addCommentReaction',
      requestPayload: { ...input },
      noResultMessage: 'addCommentReaction returned no result',
      runMutation: () =>
        this.lowLevelClient.mutation({
          addCommentReaction: {
            __args: { with: input },
            success: this.commentReactionMutationSelection(),
            errors: this.commentNotFoundErrors(),
            __typename: true,
          } satisfies Genql.MutationGenqlSelection['addCommentReaction'],
        }),
      extractResponse: (result) => result.addCommentReaction,
      buildResultPayload: this.commentReactionResultPayload,
      mapSuccess: this.mapCommentReactionSuccess,
    });
  }

  private async removeCommentReactionMutation(
    input: Genql.RemoveCommentReactionInput,
  ): Promise<TsResult<CommentReactionMutationOutput, ApiError>> {
    return this.executeMutation({
      operation: 'removeCommentReaction',
      requestPayload: { ...input },
      noResultMessage: 'removeCommentReaction returned no result',
      runMutation: () =>
        this.lowLevelClient.mutation({
          removeCommentReaction: {
            __args: { with: input },
            success: this.commentReactionMutationSelection(),
            errors: this.commentNotFoundErrors(),
            __typename: true,
          } satisfies Genql.MutationGenqlSelection['removeCommentReaction'],
        }),
      extractResponse: (result) => result.removeCommentReaction,
      buildResultPayload: this.commentReactionResultPayload,
      mapSuccess: this.mapCommentReactionSuccess,
    });
  }

  private async createComment(
    input: Genql.CreateCommentInput,
  ): Promise<TsResult<CreateCommentThreadOutput, ApiError>> {
    return this.executeMutation({
      operation: 'createComment',
      requestPayload: {
        parentType: input.parent?.type,
        threadId: input.threadId,
      },
      noResultMessage: 'createComment returned no result',
      runMutation: () =>
        this.lowLevelClient.mutation({
          createComment: {
            __args: { with: input },
            success: {
              comment: this.commentSelection,
              thread: this.commentThreadSelection,
            },
            errors: {
              ...this.commonMutationErrors,
              on_ElementsNotFoundError: this
                .genqlBaseError satisfies Genql.ElementsNotFoundErrorGenqlSelection,
              __typename: true,
            },
            __typename: true,
          } satisfies Genql.MutationGenqlSelection['createComment'],
        }),
      extractResponse: (result) => result.createComment,
      buildResultPayload: (response) => ({
        hasSuccess: response.success !== null,
        errorCount: response.errors.length,
        commentId: response.success?.comment.id,
        threadId: response.success?.thread.id,
      }),
      mapSuccess: (success) => ({
        comment: mapComment(success.comment),
        thread: mapCommentThread(success.thread),
      }),
    });
  }

  private commentNotFoundErrors() {
    return {
      ...this.commonMutationErrors,
      on_CommentNotFoundError: this
        .genqlBaseError satisfies Genql.CommentNotFoundErrorGenqlSelection,
      __typename: true,
    };
  }

  private commentReactionMutationSelection() {
    return {
      comment: this.commentSelection,
      thread: this.commentThreadSelection,
    };
  }

  private readonly commentReactionResultPayload = <
    TSuccess extends { comment: { id: string }; thread: { id: string } },
  >(
    response: MutationResponse<TSuccess>,
  ): Record<string, unknown> => ({
    hasSuccess: response.success !== null,
    errorCount: response.errors.length,
    commentId: response.success?.comment.id,
    threadId: response.success?.thread.id,
  });

  private readonly mapCommentReactionSuccess = <
    TSuccess extends {
      comment: GenqlCommentLike;
      thread: GenqlCommentThreadLike;
    },
  >(
    success: TSuccess,
  ): CommentReactionMutationOutput => ({
    comment: mapComment(success.comment),
    thread: mapCommentThread(success.thread),
  });

  private async executeMutation<TSuccess, TOutput, TResult>(params: {
    operation: CommentMutationOperation;
    requestPayload: object;
    noResultMessage: string;
    runMutation: () => Promise<TResult>;
    extractResponse: (result: TResult) => MutationResponse<TSuccess>;
    buildResultPayload: (
      response: MutationResponse<TSuccess>,
    ) => Record<string, unknown>;
    mapSuccess: (success: TSuccess) => TOutput;
  }): Promise<TsResult<TOutput, ApiError>> {
    this.logger.debug({
      context: 'ChatClient',
      msg: `Request to ${params.operation}`,
      payload: params.requestPayload,
    });

    const result = await params.runMutation();
    const response = params.extractResponse(result);

    this.logger.debug({
      context: 'ChatClient',
      msg: `Result from ${params.operation}`,
      payload: params.buildResultPayload(response),
    });

    if (response.errors.length > 0) {
      const message = response.errors.map((error) => error.message).join('; ');
      this.logger.error({
        context: 'ChatClient',
        msg: `${params.operation} failed`,
        error: response.errors,
      });
      return Err(new ApiError(message));
    }

    if (!response.success) {
      return Err(new ApiError(params.noResultMessage));
    }

    return Ok(params.mapSuccess(response.success));
  }
}
